import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";

const GROUPS = [
  { title: "Homepage Hero", fields: [
    { key: "hero_kicker", label: "Kicker (small line above title)", type: "text" },
    { key: "hero_line1", label: "Headline line 1", type: "text" },
    { key: "hero_line2", label: "Headline line 2", type: "text" },
    { key: "hero_line3", label: "Headline line 3 (red accent)", type: "text" },
    { key: "hero_subtitle", label: "Subtitle paragraph", type: "textarea" },
    { key: "hero_cta_primary_label", label: "Primary button label", type: "text" },
    { key: "hero_cta_primary_url", label: "Primary button URL", type: "url" },
  ]},
  { title: "About Us Page", fields: [
    { key: "about_us_title", label: "Page title", type: "text" },
    { key: "about_us_body", label: "Body (plain text, paragraphs separated by blank lines)", type: "textarea", rows: 10 },
    { key: "contact_email", label: "Contact email", type: "email" },
  ]},
  { title: "Footer", fields: [
    { key: "footer_tagline", label: "Tagline (small orange line)", type: "text" },
    { key: "footer_description", label: "Description paragraph", type: "textarea" },
    { key: "footer_copyright", label: "Right-side copyright text", type: "text" },
  ]},
  { title: "Cart / Checkout", fields: [
    { key: "cart_empty_message", label: "Empty cart message", type: "text" },
    { key: "cart_coupon_hint", label: "Coupon hint under input", type: "text" },
    { key: "cart_checkout_button", label: "Checkout button label", type: "text" },
  ]},
  { title: "Login & Register Pages", fields: [
    { key: "login_title", label: "Login page title", type: "text" },
    { key: "login_subtitle", label: "Login page subtitle", type: "text" },
    { key: "register_title", label: "Register page title", type: "text" },
    { key: "register_subtitle", label: "Register page subtitle", type: "text" },
  ]},
  { title: "Subscribe / Club Page", fields: [
    { key: "subscribe_kicker", label: "Kicker (small line above title)", type: "text" },
    { key: "subscribe_title", label: "Page title", type: "text" },
    { key: "subscribe_subtitle", label: "Subtitle paragraph", type: "textarea" },
  ]},
  { title: "Live Auctions Page", fields: [
    { key: "live_kicker", label: "Kicker (small red line)", type: "text" },
    { key: "live_headline_1", label: "Headline line 1", type: "text" },
    { key: "live_headline_2", label: "Headline line 2", type: "text" },
    { key: "live_subtitle", label: "Subtitle paragraph", type: "textarea" },
    { key: "live_cta_whatnot", label: "Whatnot button label", type: "text" },
    { key: "live_cta_tiktok", label: "TikTok button label", type: "text" },
    { key: "live_cta_ebay", label: "eBay button label", type: "text" },
  ]},
  { title: "Shipping (applied at Stripe checkout)", fields: [
    { key: "shipping_mode", label: "Mode — flat / free / free_over_threshold", type: "text" },
    { key: "shipping_flat_rate", label: "Flat rate ($) — used when mode=flat or below threshold", type: "number" },
    { key: "shipping_free_threshold", label: "Free-over threshold ($) — only used when mode=free_over_threshold", type: "number" },
    { key: "shipping_note", label: "Shipping note (shown near cart total, optional)", type: "text" },
  ]},
  { title: "Live Now Banner (top-of-page site-wide)", fields: [
    { key: "live_now_active", label: "Show banner NOW (true / false) — flip to 'true' when your stream goes live", type: "text" },
    { key: "live_now_label", label: "Main label (e.g. 🔴 LIVE NOW — click to join)", type: "text" },
    { key: "live_now_secondary", label: "Secondary line (optional context)", type: "text" },
    { key: "live_now_url", label: "Where clicking the banner sends visitors", type: "url" },
  ]},
  { title: "Big Hits Gallery", fields: [
    { key: "big_hits_kicker", label: "Kicker (small line above title)", type: "text" },
    { key: "big_hits_title", label: "Page + section title", type: "text" },
    { key: "big_hits_subtitle", label: "Subtitle paragraph", type: "textarea" },
  ]},
  { title: "Mystery Packs Page", fields: [
    { key: "mystery_kicker", label: "Kicker (small line above title)", type: "text" },
    { key: "mystery_title", label: "Page title", type: "text" },
    { key: "mystery_subtitle", label: "Subtitle paragraph", type: "textarea" },
  ]},
  { title: "Affiliates Page", fields: [
    { key: "affiliates_kicker", label: "Kicker (small line above title)", type: "text" },
    { key: "affiliates_title", label: "Page title", type: "text" },
    { key: "affiliates_body", label: "Intro paragraph", type: "textarea", rows: 5 },
  ]},
  { title: "Sell Us Your Cards Page", fields: [
    { key: "sell_cards_kicker", label: "Kicker (small line above title)", type: "text" },
    { key: "sell_cards_title", label: "Page title", type: "text" },
    { key: "sell_cards_body", label: "Intro paragraph", type: "textarea", rows: 5 },
  ]},
  { title: "Merch Page", fields: [
    { key: "merch_kicker", label: "Kicker (small line above title)", type: "text" },
    { key: "merch_title", label: "Page title", type: "text" },
    { key: "merch_subtitle", label: "Subtitle paragraph", type: "textarea" },
  ]},
  { title: "Merch Link (Footer)", fields: [
    { key: "merch_label", label: "Link label (default: Merch)", type: "text" },
    { key: "merch_url", label: "Link URL — leave blank to hide, use /merch for internal page, or paste an external URL", type: "text" },
  ]},
  { title: "Birthday Coupons", fields: [
    { key: "birthday_coupon_pct", label: "Discount percent (e.g. 15)", type: "number" },
    { key: "birthday_coupon_lead_days", label: "How many days before birthday to issue", type: "number" },
    { key: "birthday_coupon_valid_days", label: "How many days the coupon stays valid", type: "number" },
  ]},
  { title: "Store Sales & Stock Badges", fields: [
    { key: "low_stock_threshold", label: "Show \"Only N left\" badge when remaining stock ≤ this number (stock of 1 always shows \"LAST ONE\")", type: "number" },
  ]},
  { title: "Shipping Return Address (printed on labels)", fields: [
    { key: "return_name", label: "Business/return name (default: Card Lobster)", type: "text" },
    { key: "return_line1", label: "Street address (line 1)", type: "text" },
    { key: "return_line2", label: "Street address (line 2, optional)", type: "text" },
    { key: "return_city", label: "City", type: "text" },
    { key: "return_state", label: "State (2-letter, e.g. FL)", type: "text" },
    { key: "return_postal_code", label: "ZIP code", type: "text" },
    { key: "return_country", label: "Country code (default: US)", type: "text" },
    { key: "return_phone", label: "Phone (required by carriers)", type: "text" },
  ]},
  { title: "Social & Platform Links", fields: [
    { key: "social_whatnot_url", label: "Whatnot URL", type: "url" },
    { key: "social_tiktok_url", label: "TikTok URL", type: "url" },
    { key: "social_ebay_url", label: "eBay Store URL", type: "url" },
    { key: "social_instagram_url", label: "Instagram URL", type: "url" },
    { key: "social_youtube_url", label: "YouTube URL", type: "url" },
  ]},
];

export default function AdminContent() {
  const [values, setValues] = useState({});
  const [dirty, setDirty] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/settings");
      setValues(r.data);
    } catch { toast.error("Failed to load settings"); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const upd = (k, v) => { setValues({ ...values, [k]: v }); setDirty({ ...dirty, [k]: true }); };

  const save = async (group) => {
    const items = group.fields
      .filter((f) => dirty[f.key])
      .map((f) => ({ key: f.key, value: values[f.key] || "" }));
    if (items.length === 0) { toast.info("No changes to save"); return; }
    setSaving(true);
    try {
      await api.post("/admin/settings", { items });
      toast.success(`Saved ${items.length} field${items.length === 1 ? "" : "s"}`);
      const cleared = { ...dirty };
      items.forEach((i) => delete cleared[i.key]);
      setDirty(cleared);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <h1 className="font-display text-4xl mb-2">Site Content</h1>
      <p className="text-zinc-400 text-sm mb-8">Edit copy shown on your homepage, footer, and About page. Changes go live immediately after Save.</p>

      {GROUPS.map((g) => {
        const groupDirty = g.fields.some((f) => dirty[f.key]);
        return (
          <div key={g.title} className="border border-white/10 rounded p-6 bg-[var(--cl-surface)] mb-6" data-testid={`content-group-${g.title.replace(/\s+/g, "-").toLowerCase()}`}>
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-display text-2xl">{g.title}</h2>
              <button onClick={() => save(g)} disabled={!groupDirty || saving} className="btn-primary text-sm"
                      data-testid={`save-${g.title.replace(/\s+/g, "-").toLowerCase()}`}>
                {saving ? "Saving…" : (groupDirty ? "Save Changes" : "Saved")}
              </button>
            </div>
            <div className="space-y-4">
              {g.fields.map((f) => (
                <div key={f.key}>
                  <label className="text-label text-zinc-500">{f.label}</label>
                  {f.type === "textarea" ? (
                    <textarea rows={f.rows || 3} value={values[f.key] || ""} onChange={(e) => upd(f.key, e.target.value)}
                              data-testid={`field-${f.key}`}
                              className="mt-1 w-full bg-black border border-white/10 rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-[var(--cl-primary)]" />
                  ) : (
                    <input type={f.type} value={values[f.key] || ""} onChange={(e) => upd(f.key, e.target.value)}
                           data-testid={`field-${f.key}`}
                           className="mt-1 w-full bg-black border border-white/10 rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-[var(--cl-primary)]" />
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
